import { RelativeDateConfigModel, RelativeDateDirection, RelativeDateReference, RelativeDateUnit } from "../annualWheel/RelativeDate.types.ts";

// Pluralised by count, e.g. "1 month" / "24 months"
export const relativeDateUnitTranslationKeys: Record<RelativeDateUnit, string> = {
    [RelativeDateUnit.Days]: "relativeDateDays",
    [RelativeDateUnit.Weeks]: "relativeDateWeeks",
    [RelativeDateUnit.Months]: "relativeDateMonths",
    [RelativeDateUnit.Years]: "relativeDateYears",
};

export function getRelativeDateDescription(relativeDate: RelativeDateConfigModel, translateString: (key: string) => string): string {
    const unit = translateString(RelativeDateUnit[relativeDate.unit].toLowerCase()).toLowerCase();
    const direction = translateString(RelativeDateDirection[relativeDate.direction].toLowerCase()).toLowerCase();
    const relativeTo = (
        relativeDate.relativeTo === RelativeDateReference.ContractStart ? translateString("contractStartDate") : translateString("contractEndDate")
    ).toLowerCase();

    return `${relativeDate.value} ${unit} ${direction} ${relativeTo}`;
}

export function calculateAbsoluteDateFromRelativeConfig(
    config: RelativeDateConfigModel | undefined,
    contractStartDate: Date | null | undefined,
    contractEndDate: Date | null | undefined
): Date | null {
    if (!config) return null;

    const referenceDate = config.relativeTo === RelativeDateReference.ContractStart ? contractStartDate : contractEndDate;

    if (!referenceDate) return null;

    return shiftDate(new Date(referenceDate), config.value, config.unit, config.direction);
}

export function shiftDate(date: Date, value: number, unit: RelativeDateUnit, direction: RelativeDateDirection): Date {
    const signedValue = direction === RelativeDateDirection.After ? value : -value;

    switch (unit) {
        case RelativeDateUnit.Days:
            return addCalendarDays(date, signedValue);
        case RelativeDateUnit.Weeks:
            return addCalendarDays(date, signedValue * 7);
        case RelativeDateUnit.Months:
            return addCalendarMonths(date, signedValue);
        case RelativeDateUnit.Years:
            return addCalendarMonths(date, signedValue * 12);
    }
}

// The backend (RelativeDateConfiguration.CalculateDate) creates the tasks, so the dates shown here must match
// .NET's AddDays, AddMonths and AddYears. Counting days on the calendar rather than in milliseconds keeps a
// daylight saving change from moving the result to the previous evening.
function addCalendarDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

// .NET clamps a day the target month does not have to its last day (31 Jan + 1 month is 28 Feb), where
// setMonth alone would run over into the month after (3 Mar).
function addCalendarMonths(date: Date, months: number): Date {
    const result = new Date(date);
    const dayOfMonth = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + months);
    const lastDayOfTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(dayOfMonth, lastDayOfTargetMonth));
    return result;
}
